import * as Sentry from "@sentry/vue";
import * as SentryBrowser from "@sentry/browser";
import type { App } from "vue";
import router from "./router";
import { ENV } from "@/ENV";
import { isIntern, isProduction } from "./functions/environment";
export { generateTransactionId, transactionId } from "@/functions/transactionId";

const SENTRY_DSN = "https://93f45222c7b6567cc2479665a9359ef4@sentry.service.lisapp.de/2";
type SentryInitOptions = NonNullable<Parameters<typeof Sentry.init>[0]>;

function tracesSampler({
  name,
  inheritOrSampleWith,
}: Parameters<NonNullable<SentryInitOptions["tracesSampler"]>>[0]) {
  if (name.includes("/health") || name.includes("/version")) {
    return 0;
  }
  return inheritOrSampleWith(import.meta.env.MODE === "production" ? 0.03 : 0.15);
}

function scrubEvent(event: Parameters<NonNullable<SentryInitOptions["beforeSend"]>>[0]) {
  if (event.request) {
    delete event.request.cookies;
    delete event.request.data;
    delete event.request.query_string;
    if (event.request.headers) {
      for (const header of Object.keys(event.request.headers)) {
        if (["authorization", "cookie", "set-cookie"].includes(header.toLowerCase())) {
          delete event.request.headers[header];
        }
      }
    }
    if (event.request.url) {
      try {
        const url = new URL(event.request.url);
        url.search = "";
        url.hash = "";
        event.request.url = url.toString();
      } catch {
        event.request.url = event.request.url.split("?")[0];
      }
    }
  }
  return event;
}

export function initializeSentry(
  app: App<Element>,
  user?: {
    id?: number;
    preferredName?: string;
    username?: string;
    email?: string;
    sentrySettings?: {
      replaysSessionSampleRate?: number;
      maskAllText?: boolean;
      blockAllMedia?: boolean;
      maskAllInputs?: boolean;
    };
  },
) {
  if (import.meta.env.PROD && !import.meta.env.BASE_URL.includes("deploy-preview")) {
    let sentrySettings = user?.sentrySettings;
    if (sentrySettings != null) {
      if (typeof sentrySettings !== "object") {
        sentrySettings = {};
      }
      if (typeof sentrySettings.blockAllMedia !== "boolean") {
        sentrySettings.blockAllMedia = undefined;
      }
      if (typeof sentrySettings.maskAllText !== "boolean") {
        sentrySettings.maskAllText = undefined;
      }
      if (typeof sentrySettings.maskAllInputs !== "boolean") {
        sentrySettings.maskAllInputs = undefined;
      }
      if (
        typeof sentrySettings.replaysSessionSampleRate !== "number" ||
        sentrySettings.replaysSessionSampleRate < 0 ||
        sentrySettings.replaysSessionSampleRate > 1
      ) {
        sentrySettings.replaysSessionSampleRate = undefined;
      }
    }
    Sentry.init({
      app,
      dsn: SENTRY_DSN,
      integrations: [
        Sentry.feedbackIntegration({
          colorScheme: "system",
          autoInject: false,
          showBranding: false,
          isNameRequired: true,
          isEmailRequired: true,
        }),
        Sentry.browserTracingIntegration({
          router,
          shouldCreateSpanForRequest: (url) =>
            !url.startsWith(
              "https://9d896a63b4570fdafebf96e4384ded8b.eu.r2.cloudflarestorage.com/",
            ),
        }),
        Sentry.replayIntegration({
          blockAllMedia: sentrySettings?.blockAllMedia ?? true,
          maskAllText: sentrySettings?.maskAllText,
          maskAllInputs: sentrySettings?.maskAllInputs,
          maxReplayDuration: 60000,
          stickySession: false,
        }),
        Sentry.browserProfilingIntegration(),
      ],
      replaysSessionSampleRate: sentrySettings?.replaysSessionSampleRate ?? 0,
      replaysOnErrorSampleRate: 1.0,
      profilesSampleRate: 0.1,
      tracePropagationTargets: [window.location.origin],
      environment: ENV.VITE_APP_INSTANCE_KEY === "intern" ? "staging" : import.meta.env.MODE,
      enabled: true,
      tracesSampler,
      release: `frontend@${import.meta.env.__VERSION__}`,
      beforeSend(event) {
        const exception = event.exception?.values?.at(-1);
        return exception?.type === "AbortError" ? null : scrubEvent(event);
      },
      ignoreErrors: [
        // This error is not relevant, we'll ignore it
        // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
        "ResizeObserver loop limit exceeded",
        // Remove the errors that occur as redacted in production.
        "REDACTED",
        // "Unable to fetch dynamic resource" is a very common issue that happens if there was a LISA update deployed in the mean time.
        ...(!isProduction() && !isIntern() ? ["Unable to fetch dynamic resource"] : []),
      ],
    });
    Sentry.setTag("instance", ENV.VITE_APP_INSTANCE_KEY);
    if (user) {
      Sentry.setUser({
        id: user.id,
        username: user.preferredName ?? user.username,
        email: user.email,
      });
    }
    return Sentry;
  }
}

/** Report failures which happen before the Vue application and its Sentry integration exist. */
export function captureBootstrapException(error: unknown) {
  if (!import.meta.env.PROD || import.meta.env.BASE_URL.includes("deploy-preview")) {
    return;
  }
  if (!SentryBrowser.isInitialized()) {
    SentryBrowser.init({
      dsn: SENTRY_DSN,
      environment: ENV.VITE_APP_INSTANCE_KEY === "intern" ? "staging" : import.meta.env.MODE,
      release: `frontend@${import.meta.env.__VERSION__}`,
      beforeSend: scrubEvent,
    });
    SentryBrowser.setTag("instance", ENV.VITE_APP_INSTANCE_KEY);
  }
  SentryBrowser.captureException(error);
}

export function captureMessageInSentry(
  message: string,
  captureContext?: Parameters<typeof Sentry.captureMessage>[1],
) {
  if (import.meta.env.PROD && !import.meta.env.BASE_URL.includes("deploy-preview")) {
    Sentry.captureMessage(message, captureContext);
  }
}
